// /src/pages/publicTabs/RanksTabs.jsx

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import LoadingSpinner from '../../components/LoadingSpinner';

const RanksTab = () => {
  const publicContainerRef = useRef(null);
  const [rankGroups, setRankGroups] = useState({});
  const [loading, setLoading] = useState(true);
  const [rteContents, setRteContents] = useState({});
  const organizationId = '1';

  useEffect(() => {
    const fetchData = async () => {
      await fetchRanks();
    };
    fetchData();
  }, []);

  // load all ranks and corresponding RTE documents
  const fetchRanks = async () => {
    try {
      const response = await axios.get('/api/admin/get-org-all-ranks', {
        params: { organizationId },
        withCredentials: true,
      });

      const groupedRanks = groupRanksByType(response.data.ranks);
      setRankGroups(groupedRanks);
      const rankTypes = Object.keys(groupedRanks);

      rankTypes.push('Rank description');
      await fetchRteContents(rankTypes);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching Ranks:', error);
      setLoading(false);
    }
  };

  // inject CSS dynamically for each RTE document
  const injectStyles = (documentName, cssPublic, hash) => {
    const STYLE_ID = `style-${documentName}`;
    let styleEl = document.getElementById(STYLE_ID);
    const prevHash = styleEl?.getAttribute('data-hash') || null;

    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = STYLE_ID;

      const meta = document.querySelector('meta[name="csp-nonce"]');
      const nonce = meta && meta.content;
      if (nonce) styleEl.setAttribute('nonce', nonce);

      document.head.appendChild(styleEl);
    }

    if (hash && hash !== prevHash) {
      styleEl.textContent = cssPublic || '';
      styleEl.setAttribute('data-hash', hash);
    }
  };

  // fetch Lexical HTML + CSS for each rank type
  const fetchRteContents = async (rankTypes) => {
    const contents = {};
    try {
      for (const documentName of rankTypes) {
        const resp = await axios.get('/api/admin/lexical/retrieve-public-html-document', {
          params: { documentName },
        });

        const { htmlContent, cssPublic, contentHash } = resp.data.data;
        if (cssPublic) injectStyles(documentName, cssPublic, contentHash);

        contents[documentName] = htmlContent || '';
      }
      setRteContents(contents);
    } catch (error) {
      console.error('Error fetching HTML+CSS documents:', error);
      setRteContents({});
    }
  };

  const getTrustedHTML = (rawHTML) => {
    if (window.trustedTypes) {
      try {
        const policy = window.trustedTypes.createPolicy('pallasRanks', {
          createHTML: (input) => input,
          createScript: (input) => input,
          createScriptURL: (input) => input,
        });
        return policy.createHTML(rawHTML);
      } catch {
        return window.trustedTypes.createPolicy('pallasRanks').createHTML(rawHTML);
      }
    }
    return rawHTML;
  };

  const groupRanksByType = (ranks) => {
    const groupedRanks = {};
    ranks.forEach((rank) => {
      if (!rank.rankType) return;
      if (!groupedRanks[rank.rankType]) {
        groupedRanks[rank.rankType] = [];
      }
      groupedRanks[rank.rankType].push(rank);
    });
    return groupedRanks;
  };

  const buildHierarchy = (ranks) => {
    let nodeMap = new Map();
    let rootNodes = [];

    ranks.forEach((rank) => nodeMap.set(rank.id, { ...rank, children: [] }));

    ranks.forEach((rank) => {
      const currentNode = nodeMap.get(rank.id);
      if (rank.parentId) {
        let parentNode = nodeMap.get(rank.parentId);
        while (parentNode && !parentNode.status) {
          parentNode = nodeMap.get(parentNode.parentId);
        }
        if (parentNode) {
          parentNode.children.push(currentNode);
        } else {
          rootNodes.push(currentNode);
        }
      } else {
        rootNodes.push(currentNode);
      }
    });

    return rootNodes;
  };

  const createGradientBackground = (colors) => {
    const { borderGradient1, borderGradient2, borderGradient3, borderGradient4, borderGradient5 } = colors;
    return `linear-gradient(to right, ${borderGradient1}, ${borderGradient2}, ${borderGradient3}, ${borderGradient4}, ${borderGradient5})`;
  };

  const Rank = ({ rank }) => {
    const { rankName, payGrade, rankImageUrl, status, OrgRanksColors } = rank;
    if (!status) return null;

    const backgroundStyle = OrgRanksColors
      ? { background: createGradientBackground(OrgRanksColors) }
      : {};

    return (
      <div className={`rank-container ${payGrade}`} style={backgroundStyle}>
        <div className={`rank-content ${payGrade}`}>
          {rankImageUrl ? (
            <img className="rank-image" src={rankImageUrl} alt={`${rankName} image`} />
          ) : (
            <div className="rank-image-placeholder"></div>
          )}
          <div className="rank-name">{rankName}</div>
        </div>
      </div>
    );
  };

  const renderRanks = (ranks) => {
    const renderRank = (rank) => (
      <React.Fragment key={rank.id}>
        <Rank rank={rank} />
        {rank.children && rank.children.length > 0 && rank.children.map(renderRank)}
      </React.Fragment>
    );
    return <div className="ranks-container">{ranks.map(renderRank)}</div>;
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="ranks-tab">
      <div ref={publicContainerRef}>
        <div
          className="rank-type-description lexical-rendered-content"
          data-doc="Rank description"
          role="document"
          dangerouslySetInnerHTML={{
            __html: getTrustedHTML(rteContents['Rank description'] || ''),
          }}
        />
      </div>

      {Object.keys(rankGroups).map((rankType) => (
        <div key={rankType}>
          <div
            className="rank-type-description lexical-rendered-content"
            data-doc={rankType}
            role="document"
            dangerouslySetInnerHTML={{
              __html: getTrustedHTML(rteContents[rankType] || ''),
            }}
          />
          {renderRanks(buildHierarchy(rankGroups[rankType]))}
        </div>
      ))}
    </div>
  );
};

export default RanksTab;
